import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { HtmlDialogComponent } from 'src/app/components/dialogs/html-dialog/html-dialog.component';
import { RestService } from 'src/app/services/rest.service';
import { CommonState } from 'src/app/shared/services/common-state.service';
import { environment } from 'src/environments/environment';
import { Wiki } from '../model/wiki';

@Injectable({
  providedIn: 'root',
})
export class WikiService extends CommonState<Wiki> {
  private readonly http = inject(HttpClient);
  private readonly restService = inject(RestService);
  private readonly dialogService = inject(DialogService);

  public loadingWiki = signal(false);

  private readonly apiUrl: string = `${environment.springAppUrl}/wiki`;
  protected _currentWiki$ = new BehaviorSubject<Wiki | undefined>(undefined);
  public currentWiki$ = this._currentWiki$.asObservable() as Observable<Wiki | undefined>;

  constructor() {
    super(Wiki);
  }

  public loadAll(): void {
    this.http
      .get<{ _embedded: { wiki: Wiki[] } }>(`${this.apiUrl}`)
      .pipe(take(1))
      .subscribe((res) => {
        this._items$.next(res._embedded.wiki);
      });
  }

  public getWikiPageAndOpenDialog(pageId: number) {
    this.loadingWiki.set(true);
    this.restService.get(`/wiki/page?pageId=${pageId}`, 'text').subscribe((res) => {
      this.loadingWiki.set(false);
      const parsedBody = JSON.parse(res);
      this.dialogService.open(HtmlDialogComponent, {
        data: {
          htmlContent: parsedBody.body.styled_view.value,
        },
        header: parsedBody.title,
        closable: true,
        modal: true,
        height: '80%',
        width: '80%',
      });
    });
  }

  public urlChanged(url: string) {
    this._items$.pipe(take(1)).subscribe((items: Wiki[]) => {
      const item = items.find((i) => this.compareUrlToPattern(url, i.url));
      this._currentWiki$.next(item);
    });
  }

  private compareUrlToPattern(url: string, pattern: string): boolean {
    // Leading/trailing/duplicate slashes only produce empty segments, which are filtered out
    const normalize = (path: string) => path.trim().split('/').filter(Boolean);

    const urlSegments = normalize(url);
    const patternSegments = normalize(pattern);

    // Check segment length
    if (urlSegments.length !== patternSegments.length) return false;

    // Compare each segment
    return patternSegments.every((patternSegment, i) => {
      const urlSegment = urlSegments[i];
      return patternSegment === '*' || patternSegment === urlSegment;
    });
  }
}
